// Generated by scripts/build-rules-content.mjs from country-na.json (aligned 9 Oct 2026).
// Content for sales: plain sentences, no em dash, no person or company names.
// Edit here or regenerate; tests/rules-explainer.test.mjs checks the writing rules.
import type { CountryGuide } from "../types";

export const GUIDE_NORTH_AMERICA: CountryGuide = {
  id: "north-america",
  code: "na",
  title: "North America (USL and Canada)",
  shortName: "North America",
  entities: ["usl"],
  areaRoles: ["usl"],
  live: true,
  cardLine:
    "One company for DISA, Wheelabrator and Simpson parts, no freight on the quotation, and generic items that need the right variant.",
  summary:
    "North America is one legal entity in F&O (USL). The old USO and USS companies were merged into it, so one sales colleague often quotes DISA, Wheelabrator and Simpson parts to the same customer, sometimes in one inquiry. Canada is served from the same mailbox and entity. Mexico has its own mailbox and is not switched on in Norican I2Q yet. What makes North America different: no freight on the quotation, customer discounts that belong to Wheelabrator parts only, machine-specific generic items that need the right variant, long default lead times, and payment terms such as cash in advance. Norican I2Q runs in shadow mode: it prepares a draft, and sales always decides and sends.",
  howInquiriesArrive: [
    "Customers write to one shared parts mailbox for all brands, Norican.Parts.NA (it used to be Wheelabrator.Parts.NA). Canadian customers use the same box; you recognise them by the Canadian delivery address. Norican I2Q reads a mirror copy of this mailbox, so it sees the same mails you do and sends nothing. Today the team sorts the box by hand, by state territory, in hourly slots. Other North American boxes (for example Disa.Parts.NA, Simpson-USParts or the US RFQ box) are not read by Norican I2Q. Mexico writes to Norican.Parts.MX, which Norican I2Q does not process yet. Mail sent to a personal mailbox only reaches I2Q if it is forwarded to the shared box; in North America no automatic forwarding rule was set up. Watch out: some inquiries arrive on a purchase order form and are first read as orders. Those sit in the parked orders view, not in To review.",
  ],
  specialities: [
    {
      id: "na-mailbox-mirror",
      title: "One mailbox for all brands",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Norican I2Q picks up every new mail in the North America parts mailbox and decides whether it is an inquiry for a price or a quotation.",
      why: "Go Autonomous showed that one shared mailbox per company works, if the company comes from the mailbox the mail landed in.",
      youSee:
        "Info: inquiries from the NA box appear in To review with the area North America. Purchase orders, invoices and other mails are counted but not prepared.",
      whatToDo:
        "Keep answering from the shared mailbox as today. If a customer wrote to your personal address, forward the mail to Norican.Parts.NA so it gets prepared.",
      example:
        "A Canadian foundry writes to Norican.Parts.NA asking for prices on six wear parts. The draft shows up under North America in To review within minutes.",
      registryIds: [],
      glossaryIds: ["scope-inquiries-only"],
      source: "goa-knowledge.md part A section 3 (North America) and section 4; code: areas.py",
    },
    {
      id: "na-inquiry-read-as-order",
      title: "Inquiries that look like orders",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Every mail is first sorted as inquiry, purchase order or other. Only inquiries get a draft quotation.",
      why: "In North America many customers send a price request on their purchase order form, so the first sort sometimes reads an inquiry as an order.",
      youSee: "Info: such mails are not in To review. They sit in the parked orders view.",
      whatToDo:
        "If a customer is waiting for a quotation and you cannot find the draft, look in the parked orders view. Quote by hand as today.",
      example:
        'A customer sends a PO form with the note "please quote before we release". It lands in parked orders, not in To review.',
      registryIds: [],
      glossaryIds: ["scope-inquiries-only"],
      source: "scope.md, section Known gap: inquiries labelled as orders",
    },
    {
      id: "na-territory-owner",
      title: "Who picks up the inquiry",
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks:
        "Planned: Norican I2Q suggests the owner of each inquiry from the customer's sales unit or the state territory.",
      why: 'In shared mailboxes users asked "what is for me?", and the US team sorts its box every hour by state territory.',
      youSee:
        "Today: you assign the inquiry yourself in the queue. Planned: a suggested owner by territory.",
      whatToDo:
        "Until then, assign the inquiry to yourself or the colleague who owns the territory, as you do in the mailbox.",
      example:
        "An inquiry from a customer in Georgia: today you assign it by hand; later Norican I2Q will suggest the colleague for the Southeast territory.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md 2.4 Draft owner, backlog S5; transcript GoA US-DE Shared mailbox setup [00:00]-[00:06]",
    },
    {
      id: "na-end-customer",
      title: "Quote the end customer, not the reseller",
      status: "active",
      tone: "info",
      topic: "customer",
      checks:
        'When a reseller, trader or distributor names the plant it writes for, Norican I2Q shows both and marks the end customer "Quoted first". The reseller stays on the page. When sales quoted most earlier mails of this reseller on its own account, the reseller comes first (product owner, 14 October 2026). Planned: the reseller as the invoice account.',
      why: "US sales asked Go Autonomous to quote the end customer with the reseller as invoice account, but it could pick only one account.",
      youSee:
        'The end customer shows as certain only when the mail names it and it was quoted for this sender before; otherwise "Suggested, please confirm". When no account of it is found, the open point says "I2Q found no account in F&O for the end customer.".',
      whatToDo:
        "When a reseller writes on behalf of a plant, check the suggested account and confirm or change it to the end customer before you use the prices.",
      example:
        "An industrial supplier asks for blast wheel parts \"for our customer's plant in Indiana\". Check that the draft uses the plant's account, not the supplier's.",
      registryIds: [],
      glossaryIds: ["agents-and-distributors"],
      source:
        "goa-knowledge.md 2.1 Per-country customer policy (USL 7), section 3 North America, backlog M9",
    },
    {
      id: "na-portal-customers",
      title: "Customers who buy through a portal",
      status: "active",
      tone: "blocked",
      topic: "customer",
      checks:
        "Whether the customer or the sender's domain is on the portal list for quotations. The 12 North American portal customers are listed for portal orders only. One car maker's supplier portal is listed for quotations in every country.",
      why: "Go Autonomous kept a list of portal customers per country (12 for North America), because a mail quotation is useless when the customer only accepts portal answers.",
      youSee:
        'Blocked only for the portal listed for quotations: "This mail comes from a supplier portal that is not used for quotations by mail." An inquiry from one of the 12 customers is not held. The customer card shows the buyer, marked "Procurement portal".',
      whatToDo:
        "For a portal customer, answer in the portal as today and use the draft only as a price check.",
      example:
        'A portal sends "New RFQ from a large automotive foundry". The draft shows the foundry as customer; you still answer in the portal.',
      plannedPart:
        "The North American portal customers whose quotations must go through the portal.",
      registryIds: ["gate.portal"],
      glossaryIds: ["portal-customers"],
      modes: {
        "gate.portal": {
          usl: "block",
        },
      },
      source:
        "goa-knowledge.md 2.1 Portal customers; List customers uses Portal.xlsx (USA), backlog M4",
    },
    {
      id: "na-competitor",
      title: "Mail from competitors",
      status: "active",
      tone: "blocked",
      topic: "customer",
      checks: "Mail from a domain on the competitor list, also when a colleague forwarded it.",
      why: "Norican quotes competitors higher or not at all, and in North America one competitor is also a customer and four are also suppliers.",
      youSee:
        'Blocked: "The sender\'s domain is on the competitor list." The case is held and the queue shows this reason first. For the competitors that are also a customer or a supplier, it is a warning: "... but this company is also a customer of this entity."',
      whatToDo:
        "Handle this inquiry by hand and quote only if your manager agrees. For a listed customer, check that this is a genuine customer inquiry before you quote.",
      example:
        "A shot-blast service company that also sells competing equipment asks for 20 liners. The case is held with the competitor reason.",
      registryIds: ["gate.competitor"],
      glossaryIds: ["competitor-domains"],
      modes: {
        "gate.competitor": {
          usl: "block",
        },
      },
      source: "goa-knowledge.md 2.1 Competitor domains; backlog M3",
    },
    {
      id: "na-brand-discount",
      title: "Customer discount is for Wheelabrator parts only",
      status: "planned",
      tone: "warning",
      topic: "price",
      checks:
        "Planned: Norican I2Q flags DISA and Simpson lines that still carry the customer discount.",
      why: "Since the merge the customer discount, meant for Wheelabrator parts, also lands on DISA and Simpson lines, and sales removed it by hand.",
      youSee:
        "Today: the price shown is the customer's price from F&O, discount included. Planned: a warning on DISA and Simpson lines that carry the discount.",
      whatToDo:
        "On a mixed inquiry, check every DISA and Simpson line and take the customer discount off where it does not belong.",
      example:
        "A foundry asks for 4 Wheelabrator blades and 2 DISA moulding parts. The 10 % customer discount stays on the blades and comes off the 2 DISA lines.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md 2.3 Customer discounts (USL); Issue Log #37; transcript USL - Review Quote to Order [00:04]-[00:08]; backlog S10",
    },
    {
      id: "na-price-source",
      title: "Prices come from F&O only",
      status: "active",
      tone: "info",
      topic: "price",
      checks:
        "Norican I2Q takes the price from the customer's price list in F&O (customer-specific price first, else the customer's price group), valid on the day and for the quantity asked.",
      why: "Go Autonomous showed that a price not taken from F&O's own price lists leads to wrong quotations.",
      youSee: "Info: the price on each line, and the price group used, in the quotation preview.",
      whatToDo:
        "Compare with what the customer expects. If the customer quotes an older price, decide yourself whether to honour it.",
      example:
        "A customer asks for 50 bolts at \"last year's price\". The draft shows today's list price; you decide whether to accept the old one.",
      registryIds: ["pricing.no_price"],
      glossaryIds: ["never-zero-price"],
      modes: {
        "pricing.no_price": {
          usl: "block",
        },
      },
      source: "goa-knowledge.md 2.3 Price source; code: pricing/lookup.py",
    },
    {
      id: "na-missing-price",
      title: "No price means no automatic line",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks:
        "Norican I2Q never puts a zero or empty price on a line. A line without a valid price in F&O is held back for you.",
      why: "North American sales remove a line without a price and quote the rest, because a zero price must never reach a customer.",
      youSee:
        'Blocked: the line shows "No price", and the prepared quotation says "No price was found for ...". The other lines still show.',
      whatToDo:
        "Quote the lines that have a price now. Ask for a price for the missing item and send it in a second answer, as you do today.",
      example:
        "Of 8 lines, 1 obsolete hanger has no price. Send the 7 priced lines today and follow up on the hanger.",
      registryIds: ["pricing.no_price"],
      glossaryIds: ["never-zero-price"],
      modes: {
        "pricing.no_price": {
          usl: "block",
        },
      },
      source:
        "goa-knowledge.md part B section 2 US, Missing price; code: fo/preconditions.py check_prices",
    },
    {
      id: "na-margin-check",
      title: "Margin check",
      status: "active",
      tone: "blocked",
      topic: "price",
      checks:
        "Where the cost is known, Norican I2Q compares the line's contribution ratio (how much of the price is left after cost) with the margin threshold for North America.",
      why: "Go Autonomous stopped low-margin quotations, and US sales asked for a warning they can release instead.",
      youSee:
        'Blocked until a manager approves: the line shows "Margin: needs approval". If F&O has no cost for the item, the line shows "Margin: not checked (no cost in F&O)", and the prepared quotation is held as well. Only colleagues with margin rights see the figures. Everyone else sees the result only.',
      whatToDo:
        'Check the price. If it is right, ask your manager to release it with "Approve price". If the item, price, quantity or customer changes later, the approval must be given again. You can also change the price.',
      example:
        'A large customer price for a rubber belt leaves less margin than the threshold. The line shows "Margin: needs approval" until a manager approves it.',
      registryIds: ["pricing.margin"],
      glossaryIds: ["contribution-ratio"],
      modes: {
        "pricing.margin": {
          usl: "block",
        },
      },
      source:
        'goa-knowledge.md 2.3 Contribution ratio gate (USL CR); code: pricing/thresholds.py; help content "Prices and margin check"',
    },
    {
      id: "na-blocked-items",
      title: "Items that cannot be sold",
      status: "active",
      tone: "blocked",
      topic: "items",
      checks:
        "The item's status in F&O. Obsolete, blocked or on-hold items are not quoted. Phased-out items are shown with their replacement where F&O has one. US items also use the codes PRO-Update and PUR-Update.",
      why: "Go Autonomous quoted phased-out items and missed replacements.",
      youSee:
        'Blocked: "Blocked for sale in F&O" on the line. Warning: "Phase-out item", with the replacement number when F&O has one. Warning for PRO-UPDATE and PUR-UPDATE items: "Line 2 has signal code PRO-UPDATE, which needs a check before quoting."',
      whatToDo:
        "Offer the replacement if there is one, or ask engineering or purchasing for the right part.",
      example:
        "A customer asks for an old impeller marked OBSOLETE. The line is not priced; you offer the successor part instead.",
      registryIds: ["gate.signal_review", "gate.successor"],
      glossaryIds: ["signal-codes", "successor-items"],
      modes: {
        "gate.signal_review": {
          usl: "warn",
        },
        "gate.successor": {
          usl: "warn",
        },
      },
      source:
        "goa-knowledge.md 2.2 Signal codes (USL adds PRO-Update, PUR-Update, OBSOLETE, Generic); code: pricing/lookup.py",
    },
    {
      id: "na-generic-items",
      title: "Generic machine parts need the variant",
      status: "active",
      tone: "warning",
      topic: "items",
      checks:
        "Whether a matched item has the signal code GENERIC in F&O, which means it stands for several machine variants (for example STD, STRIP+COMP or SPEC).",
      why: "Without the variant Go Autonomous picked the base item, which has no price, and about 1 in 10 of these requests carries a wrong code.",
      youSee:
        'Warning: "Line 2 is a generic item without a machine variant." It is a warning, not a block, because sales sometimes quote the generic item as it is.',
      whatToDo:
        "Ask the customer for the machine variant and quote that item. Look up the machine serial number in CRM if the customer does not know it.",
      example:
        'A customer asks for a "blast wheel liner set" with no machine named. Ask for the serial number before quoting the STD or SPEC version.',
      registryIds: ["gate.signal_generic"],
      glossaryIds: ["signal-codes"],
      modes: {
        "gate.signal_generic": {
          usl: "warn",
        },
      },
      source:
        "goa-knowledge.md 2.2 Machine variants; part B section 2 US, Generic items; transcript USL - Review Quote to Order [00:42]-[00:49]",
    },
    {
      id: "na-lead-time",
      title: "Lead time and ship date",
      status: "active",
      tone: "warning",
      topic: "price",
      checks:
        "Norican I2Q warns when the F&O lead time is longer than 99 days or the line ships after the date the customer asked for.",
      why: "In North America the default lead time for non-stock parts is about six months, so the date F&O gives is often not the real date.",
      youSee:
        'Warning: "Line 2 has a lead time of 180 days." or a ship date after the requested date. I2Q does not have the F&O lead times yet, so you rarely see these warnings today. The check for a price without a lead time is off.',
      whatToDo:
        "For non-stock items, check stock in Denmark as today, then add 4 to 5 weeks of air freight. Stock parts ship in about 2 days, local purchase takes 6 to 7 weeks.",
      example:
        "F&O shows 180 days for a DISA part. Denmark has it in stock, so you quote about 5 weeks instead.",
      plannedPart: "Lead times from F&O, so these warnings show reliably.",
      registryIds: ["pricing.lead_time", "pricing.after_requested", "pricing.ship_today"],
      glossaryIds: ["lead-time-warnings"],
      modes: {
        "pricing.lead_time": {
          usl: "warn",
        },
        "pricing.after_requested": {
          usl: "warn",
        },
        "pricing.ship_today": {
          usl: "off",
        },
      },
      source:
        "goa-knowledge.md 2.3 Lead time (US > 99 days, NG-38); part B section 2 US, Lead time; backlog S4",
    },
    {
      id: "na-freight",
      title: "No freight on the quotation",
      status: "active",
      tone: "info",
      topic: "freight",
      checks:
        "Whether a freight line belongs on the quotation. For North America it does not: freight is added at invoicing.",
      why: "In North America freight is added at invoicing or the customer ships on its own carrier account, so only cash-in-advance customers need freight up front.",
      youSee:
        'Info: the preview\'s freight says "Does not apply". A note says "Payment terms are not in master data, so a customer who pays in advance is not recognised."',
      whatToDo:
        "Normally nothing. Add freight by hand when the customer pays in advance; ask the shipping department for an estimate.",
      example:
        "A new customer on cash in advance asks for 3 parts. Get the parcel or truck freight estimate from shipping and add it before you send.",
      plannedPart: "Recognise cash-in-advance customers from their payment terms and warn on them.",
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          usl: "warn",
        },
      },
      source: "goa-knowledge.md 2.5 Freight elsewhere; 9.6 Freight (#38); 9.3 three freight models",
    },
    {
      id: "na-tariff-surcharge",
      title: "Tariff surcharge",
      status: "active",
      tone: "info",
      topic: "mail-and-case",
      checks: "Whether I2Q adds the US tariff surcharge. It does not, and this is not planned.",
      why: "In Go Autonomous the 3 % tariff surcharge was added by hand or through a separate price list, and tariffs belong in the price lists.",
      youSee: "Info: no tariff surcharge line in the preview.",
      whatToDo: "Add the surcharge yourself where it applies, as today.",
      example:
        "A quotation for imported DISA parts: add the 3 % tariff surcharge by hand before you send.",
      registryIds: [],
      glossaryIds: [],
      source:
        "goa-knowledge.md 2.5 Freight elsewhere (3 % tariff surcharge by hand); 9.6 (#57, PL103)",
    },
    {
      id: "na-payment-terms",
      title: "Payment terms, cash in advance",
      status: "planned",
      tone: "warning",
      topic: "freight",
      checks:
        "Planned: Norican I2Q shows the customer's payment terms from F&O on the quotation and flags terms that mean payment first. Some terms are codes, not a number of days (CIA, PIA, CCC, TPC, DUR, PPD).",
      why: "Some North American payment terms mean payment before delivery, and these customers need a pro-forma with freight up front.",
      youSee:
        "Today: payment terms are not on the preview, and a note says a customer who pays in advance is not recognised. Planned: the terms, and a warning when they mean payment before delivery.",
      whatToDo:
        "Check the customer's terms in F&O. For cash in advance, make sure freight is included and the customer knows payment comes first.",
      example:
        "A customer with term CIA asks for a quotation. Quote with freight included and state that payment is due before shipment.",
      registryIds: ["pricing.freight"],
      glossaryIds: ["freight"],
      modes: {
        "pricing.freight": {
          usl: "warn",
        },
      },
      source:
        "goa-knowledge.md 2.5 Payment terms (USO PPD), section 3 North America (CIA, PIA, CCC, TPC, DUR); NA Payment Terms.xlsx; backlog M2",
    },
    {
      id: "na-minimum-order",
      title: "Minimum order value",
      status: "planned",
      tone: "info",
      topic: "price",
      checks: "Planned: a hint when the quotation total is below USD 250.",
      why: "About 1 in 8 former USO orders in 2024 were below the minimum, and F&O has no rule for it.",
      youSee: 'Planned: info, "below minimum order value" on the draft.',
      whatToDo: "Decide whether to add the minimum charge or suggest the customer adds parts.",
      example:
        "A customer asks for 2 washers worth USD 40. Point out the USD 250 minimum in your answer.",
      registryIds: [],
      glossaryIds: ["minimum-order-value"],
      source: "goa-knowledge.md 9.6 Minimum order value (USO USD 250, #26); backlog S16",
    },
    {
      id: "na-validity",
      title: "How long the quotation is valid",
      status: "planned",
      tone: "info",
      topic: "quotation",
      checks: "Planned: one validity per entity, the same on the quotation and in the cover mail.",
      why: "The North America reply template says 30 days, the old USO rule was 3 months, and Go Autonomous used different values in F&O and in the mail.",
      youSee: "Today: no validity is set on the preview. Planned: the validity of North America.",
      whatToDo: "Write the validity you use today in your answer.",
      example:
        'Your cover mail says "valid for 30 days"; make sure the F&O quotation says the same.',
      registryIds: [],
      glossaryIds: ["quotation-validity"],
      source: "goa-knowledge.md 2.5 Validity / expiry; backlog S3",
    },
    {
      id: "na-cover-mail",
      title: "Reply draft and language",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks: "Norican I2Q writes the reply draft in the language of the customer's mail.",
      why: "Customers answer in their own language, and the default document language is English (US) for North America and Spanish (Mexico) for Mexico.",
      youSee:
        "Info: a reply draft in the customer's language, with a generic greeting. The preview shows the document language: the customer's, else English (US).",
      whatToDo:
        "Check the greeting and the text, add any machine or serial number notes, and send it yourself from the shared mailbox.",
      example:
        "A customer in Quebec writes in French. The reply draft is in French, and the preview shows the language set on the customer's account.",
      registryIds: [],
      glossaryIds: ["translation"],
      source:
        "goa-knowledge.md 2.5 Quote document language and Reply language; section 5 Cover mail (US asks for a PO back)",
    },
    {
      id: "na-sales-pool",
      title: "Sales pool follows the main brand",
      status: "active",
      tone: "warning",
      topic: "quotation",
      checks:
        "Norican I2Q proposes the sales pool and the CRM fields (division, business type, sales product) from the item group with the most lines.",
      why: "In North America one quotation can mix DISA, Wheelabrator and Simpson, and the pool must follow the main business line.",
      youSee:
        "The quotation preview shows the proposed pool. Warning on a tie between item groups, or when a group has no pool. A note names the item group with the highest value when it differs.",
      whatToDo:
        'Check the pool in F&O. Service-visit parts go to pool AMSW; "Expedite" or "Shutdown" requests go to the supply-chain pool.',
      example:
        "5 Wheelabrator lines and 2 Simpson lines: the proposed pool is the Wheelabrator one (AM-WB).",
      registryIds: ["header.pool"],
      glossaryIds: ["quotation-header"],
      modes: {
        "header.pool": {
          usl: "warn",
        },
      },
      source:
        "goa-knowledge.md 2.4 Item group to CRM fields to sales pool; Open pool decisions (USL AMSW, Expedite/Shutdown); backlog M1",
    },
    {
      id: "na-sales-unit",
      title: "Sales responsible and sales taker",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "Planned: Norican I2Q reads the customer's sales unit (sales responsible and sales taker) and warns when it is missing or points to someone who has left.",
      why: "Go Autonomous failed when sales units were out of date, and the merge created units that need more than two people.",
      youSee:
        'Info: "Sales unit, sales responsible, sales taker, payment terms, delivery terms and delivery mode are not available yet." I2Q does not have the customer\'s sales unit yet.',
      whatToDo:
        "Check the sales unit in F&O before you create the quotation, and tell the master data owner when it is wrong.",
      example:
        "A customer still has a sales unit with a colleague who left last year. Planned: I2Q warns; today you fix it in F&O.",
      plannedPart:
        "The sales unit on the preview, and a warning when it is missing or out of date.",
      registryIds: ["header.not_available"],
      glossaryIds: ["quotation-header"],
      modes: {
        "header.not_available": {
          usl: "info",
        },
      },
      source:
        "goa-knowledge.md 2.4 Sales unit (USL 36 rows); section 3 North America (Issue Log FR #30); backlog M2",
    },
    {
      id: "na-header-fields",
      title: "Quotation header fields",
      status: "active",
      tone: "info",
      topic: "quotation",
      checks:
        "Norican I2Q prepares the customer, the currency, the lines in the order of the inquiry, the sales pool and the customer reference from the mail.",
      why: "US sales use the reference fields their own way, and at USO go-live Go Autonomous kept asking for the delivery address.",
      youSee:
        "Info: the header fields in the quotation preview. Fields that are not filled yet are empty, and a note lists them.",
      whatToDo:
        "Fill the empty fields in F&O as today. Use the delivery address from the mail when the customer names one.",
      example:
        'The inquiry says "ship to our Texas plant". Check that the delivery address is the Texas plant, not the head office.',
      plannedPart:
        "Delivery address, payment terms, delivery terms and mode of delivery from the customer account and the mail.",
      registryIds: ["header.pool", "header.not_available"],
      glossaryIds: ["quotation-header", "customer-reference-fields"],
      modes: {
        "header.pool": {
          usl: "warn",
        },
        "header.not_available": {
          usl: "info",
        },
      },
      source:
        "goa-knowledge.md section 5 Header fields; part B section 2 US, Quote entry and Delivery address; 2.5 Customer requisition / reference",
    },
    {
      id: "na-large-quote",
      title: "Large quotations",
      status: "active",
      tone: "info",
      topic: "price",
      checks:
        "Whether the quotation total is above an approval limit. Limits exist for EUR and GBP only, so a quotation in USD is not checked yet.",
      why: "Go Autonomous sent quotations above 50,000 to manual handling, and the US asked for 100,000 instead.",
      youSee:
        'Note on a USD quotation: "No approval limit is set for USD, so the total is not checked."',
      whatToDo:
        "Ask your manager whether this quotation needs an approval, and follow your local approval rule.",
      example:
        "A quotation for a full set of wear parts totals USD 120,000. The case notes that no limit is set for USD, and you follow your approval rule.",
      plannedPart:
        "An approval limit for USD. Go Autonomous used 50,000 and the US asked for 100,000; this is not decided.",
      registryIds: ["gate.doa_limit"],
      glossaryIds: ["large-quotation"],
      modes: {
        "gate.doa_limit": {
          usl: "warn",
        },
      },
      source: "goa-knowledge.md 2.3 Delegation of authority (Issue Log #11)",
    },
    {
      id: "na-commodity-code",
      title: "Missing commodity codes",
      status: "planned",
      tone: "info",
      topic: "mail-and-case",
      checks: "Planned: a hint when an item has no commodity (customs) code.",
      why: "About 55 % of USL parts have no commodity code, which matters for export and for customs on imported parts.",
      youSee: "Planned: a hint on the line.",
      whatToDo:
        "For export quotations, check the code with the master data owner before the customer needs it.",
      example: "A Mexican customer needs the customs code for 4 parts; 2 have none in F&O.",
      registryIds: [],
      glossaryIds: [],
      source: "goa-knowledge.md section 3 North America; Parts Missing Commodity Codes (USL).xlsx",
    },
  ],
};
